import { useEffect, useRef } from 'react';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import { normalizeChannelHandle } from '@jwp/ott-common/src/utils/channelHandle';
import {
  GET_MY_CHANNEL,
  IS_CHANNEL_AVAILABLE,
  type GetMyChannelData,
  type IsChannelAvailableData,
} from '@jwp/ott-common/src/graphql/query/channel.queries';

/**
 * Fetches isChannelAvailable once after login and stores it in AccountStore.
 * When the user already has a channel, also caches the handle for My Channel navigation.
 */
export const useChannelAvailability = () => {
  const userId = useAccountStore((state) => state.user?.id);
  const isChannelAvailable = useAccountStore((state) => state.isChannelAvailable);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!userId) {
      const state = useAccountStore.getState();
      if (state.isChannelAvailable !== null || state.myChannelHandle !== null || state.myChannelId !== null) {
        useAccountStore.setState({
          isChannelAvailable: null,
          myChannelHandle: null,
          myChannelId: null,
        });
      }
      return;
    }

    if (isChannelAvailable !== null || inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;

    const load = async () => {
      try {
        const client = getAuthApolloClient();
        const availabilityResult = await client.query<IsChannelAvailableData>({
          query: IS_CHANNEL_AVAILABLE,
          fetchPolicy: 'network-only',
        });
        const available = Boolean(availabilityResult.data?.isChannelAvailable);

        if (!available) {
          useAccountStore.setState({
            isChannelAvailable: false,
            myChannelHandle: null,
            myChannelId: null,
          });
          return;
        }

        let handle: string | null = null;
        let chId: string | null = null;
        try {
          const channelResult = await client.query<GetMyChannelData>({
            query: GET_MY_CHANNEL,
            fetchPolicy: 'network-only',
          });
          handle = normalizeChannelHandle(channelResult.data?.getMyChannel?.channels?.[0]?.chHandle || '') || null;
          chId = channelResult.data?.getMyChannel?.channels?.[0]?.chId || null;
        } catch (error) {
          console.error('Failed to preload my channel handle', error);
        }

        useAccountStore.setState({
          isChannelAvailable: true,
          myChannelHandle: handle,
          myChannelId: chId,
        });
      } catch (error) {
        console.error('Failed to load channel availability', error);
        useAccountStore.setState({
          isChannelAvailable: false,
          myChannelHandle: null,
          myChannelId: null,
        });
      } finally {
        inFlightRef.current = false;
      }
    };

    void load();
  }, [userId, isChannelAvailable]);
};
